/**
 * TasksSidebarPanel — Compact bottom panel for sidebar with 2 tabs
 *
 * Shows In Progress / Complete tabs with scrollable task list.
 * Expandable up to maxHeight. Has "mark all viewed" and "open fullscreen" actions.
 * Designed to sit in the sidebar footer slot.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Badge,
  List,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TaskItem } from './TaskItem';
import { TaskDetailDialog } from './TaskDetailDialog';
import type { TaskResultActionsRenderer } from './TaskDetailDialog';
import { useTasks } from '../hooks/useTasks';
import { useTaskActions } from '../hooks/useTaskActions';
import { useTaskStore } from '../store/taskStore';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';
import { ACTIVE_STATUSES, TERMINAL_STATUSES } from '../types';
import type { BackendTask, TaskFilter, TaskRowActionsRenderer } from '../types';

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 200;

type SidebarTab = 'inProgress' | 'complete';

export interface TasksSidebarPanelProps {
  /** Whether sidebar is collapsed (show minimal UI) */
  collapsed?: boolean;
  /** Open fullscreen tasks view */
  onOpenFullscreen?: () => void;
  /** Optional entity scope (e.g. { strategyId }, { strategyId, blockId }) */
  filter?: TaskFilter;
  /**
   * If true, render `null` when there are zero matching tasks.
   * Default `true` preserves the sidebar-footer behavior. Set to `false`
   * when the panel is anchored to a fixed position (e.g. canvas corner)
   * and must remain visible so users see new tasks as they appear.
   */
  hideWhenEmpty?: boolean;
  /** Optional module-specific actions rendered inside TaskDetailDialog. */
  renderResultActions?: TaskResultActionsRenderer;
  /** Optional module-specific actions rendered directly in task rows. */
  renderRowActions?: TaskRowActionsRenderer;
}

export const TasksSidebarPanel: React.FC<TasksSidebarPanelProps> = ({
  collapsed = false,
  onOpenFullscreen,
  filter,
  hideWhenEmpty = true,
  renderResultActions,
  renderRowActions,
}) => {
  const [activeTab, setActiveTab] = useState<SidebarTab>('inProgress');
  const [expanded, setExpanded] = useState(false);
  // Detail dialog mounted inside the panel — clicking a task row opens the
  // same `<TaskDetailDialog>` that the fullscreen `<TasksView>` uses, so the
  // user no longer has to navigate to the all-tasks list to inspect one task.
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);

  const navigate = useNavigate();
  const currentUserId = useTaskStore((s) => s.currentUserId);
  const { cancelTask, removeTask, markViewed, navigateToResult } = useTaskActions();

  // Override ownerMode to 'all' — show all project tasks, not just mine
  // Override statusMode to prevent store default ('active') from interfering
  const scopedTasks = useTasks({
    ...filter,
    ownerMode: 'all',
    statusMode: 'all',
    sortBy: 'createdAt',
    sortDirection: 'asc',
  });

  const inProgressTasks = useMemo(
    () => scopedTasks.filter((task) => ACTIVE_STATUSES.includes(task.status)),
    [scopedTasks],
  );

  const completeTasks = useMemo(
    () => scopedTasks.filter((task) => TERMINAL_STATUSES.includes(task.status)),
    [scopedTasks],
  );

  const unviewedCount = useMemo(() => {
    if (!currentUserId) return 0;
    return completeTasks.filter((task) => !task.viewedBy?.includes(currentUserId)).length;
  }, [completeTasks, currentUserId]);

  const tasks = activeTab === 'inProgress' ? inProgressTasks : completeTasks;

  const handleMarkAllViewed = useCallback(() => {
    completeTasks.forEach((task) => {
      if (currentUserId && !task.viewedBy?.includes(currentUserId)) {
        markViewed(task.id);
      }
    });
  }, [completeTasks, currentUserId, markViewed]);

  const isUnviewed = useCallback(
    (task: BackendTask) => {
      if (!currentUserId) return false;
      return !task.viewedBy?.includes(currentUserId);
    },
    [currentUserId]
  );

  const panelHeight = expanded ? MAX_HEIGHT : MIN_HEIGHT;

  // Don't render if no tasks at all (opt-in via hideWhenEmpty — sidebar
  // footer hides itself, canvas corners stay anchored and show an empty state).
  const totalTasks = inProgressTasks.length + completeTasks.length;
  if (hideWhenEmpty && totalTasks === 0) return null;

  // Collapsed sidebar — just show count indicator
  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Badge badgeContent={inProgressTasks.length || unviewedCount} color="primary" max={99}>
          <Typography variant="caption" sx={{ fontSize: 10 }}>
            Tasks
          </Typography>
        </Badge>
      </Box>
    );
  }

  return (
    <Box
      data-testid={TASKS_PANEL.sidebarPanel}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      {/* Header: tabs + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, val: SidebarTab) => setActiveTab(val)}
          sx={{
            minHeight: 32,
            flex: 1,
            '& .MuiTab-root': { minHeight: 32, py: 0, px: 1, fontSize: '0.7rem', textTransform: 'none' },
          }}
        >
          <Tab
            value="inProgress"
            label={
              <Badge badgeContent={inProgressTasks.length} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}
              >
                <span>In Progress</span>
              </Badge>
            }
            data-testid={TASKS_PANEL.sidebarTabInProgress}
          />
          <Tab
            value="complete"
            label={
              <Badge badgeContent={unviewedCount} color="error" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}
              >
                <span>Complete</span>
              </Badge>
            }
            data-testid={TASKS_PANEL.sidebarTabComplete}
          />
        </Tabs>

        {/* Mark all viewed */}
        {activeTab === 'complete' && unviewedCount > 0 && (
          <Tooltip title="Mark all as viewed">
            <IconButton
              size="small"
              onClick={handleMarkAllViewed}
              data-testid={TASKS_PANEL.markAllViewed}
            >
              <DoneAllIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Open fullscreen */}
        {onOpenFullscreen && (
          <Tooltip title="Open fullscreen">
            <IconButton
              size="small"
              onClick={onOpenFullscreen}
              data-testid={TASKS_PANEL.sidebarFullscreen}
            >
              <FullscreenIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Expand/collapse toggle */}
        <IconButton
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          data-testid={TASKS_PANEL.sidebarExpand}
        >
          {expanded ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>

      {/* Task list */}
      <Box
        sx={{
          maxHeight: panelHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: 'max-height 0.2s ease',
        }}
      >
        {tasks.length > 0 ? (
          <List dense sx={{ py: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {tasks.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                isUnviewed={isUnviewed(task)}
                compact
                showNavigation
                onCancel={cancelTask}
                onRemove={removeTask}
                onNavigate={navigateToResult}
                onMarkViewed={markViewed}
                onShowDetails={(id) => setDetailTaskId(id)}
                renderRowActions={renderRowActions}
              />
            ))}
          </List>
        ) : (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {activeTab === 'inProgress' ? 'No running tasks' : 'No completed tasks'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Detail dialog — same component the fullscreen `<TasksView>` mounts.
          Clicking any row opens it in place; users no longer need to leave
          the floating panel to inspect a task's phases / result / errors. */}
      <TaskDetailDialog
        taskId={detailTaskId}
        open={detailTaskId !== null}
        onClose={() => setDetailTaskId(null)}
        renderResultActions={renderResultActions}
        onNavigate={(frontendUrl) => {
          // Mirror `TasksView.handleDetailNavigate` — SPA navigate via
          // react-router (no full page reload) and close the dialog so the
          // user doesn't return to a stale modal on the destination page.
          if (frontendUrl) navigate(frontendUrl);
          setDetailTaskId(null);
        }}
      />
    </Box>
  );
};
